import React, { useState, useEffect } from 'react';
import { Eye, RefreshCw, Calculator, DollarSign, Copy, CheckCircle, AlertCircle, Info } from 'lucide-react';
import apiService from '../../../services/api';
import { GeneratedPrompt, AnalysisFramework, PromptTemplate } from '../../../types/prompt';
import { highlightVariables } from '../../../utils/prompt-helpers';

interface NotificationState {
  type: 'success' | 'error' | null;
  message: string;
}

interface CountryOption {
  code: string;
  name: string;
}

export default function PromptPreview() {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [frameworks, setFrameworks] = useState<AnalysisFramework[]>([]);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<NotificationState>({ type: null, message: '' });
  
  const [selectedCountry, setSelectedCountry] = useState('EST');
  const [selectedFramework, setSelectedFramework] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedTier, setSelectedTier] = useState<number>(2);
  const [generatedPrompt, setGeneratedPrompt] = useState<GeneratedPrompt | null>(null);
  const [tokenEstimate, setTokenEstimate] = useState<number>(0);
  const [costEstimate, setCostEstimate] = useState<number>(0);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (notification.type) {
      const timer = setTimeout(() => {
        setNotification({ type: null, message: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchInitialData = async () => {
    try {
      const [countriesRes, frameworksRes, templatesRes] = await Promise.all([
        apiService.get('/api/countries'),
        apiService.get('/api/admin/prompts/frameworks'),
        apiService.get('/api/admin/prompts/templates')
      ]);
      
      setCountries(countriesRes.data);
      setFrameworks(frameworksRes.data);
      setTemplates(templatesRes.data);
      
      // Set default selections
      if (frameworksRes.data.length > 0) {
        setSelectedFramework(frameworksRes.data[0].code);
      }
      if (templatesRes.data.length > 0) {
        setSelectedTemplate(templatesRes.data[0].code);
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to load data' });
    }
  };

  const generatePreview = async () => {
    if (!selectedCountry || !selectedFramework || !selectedTemplate) {
      setNotification({ type: 'error', message: 'Please select country, framework, and template' });
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.post('/api/admin/prompts/analysis-prompts/preview', {
        countryCode: selectedCountry,
        frameworkCode: selectedFramework,
        templateCode: selectedTemplate,
        tier: selectedTier,
      });
      
      setGeneratedPrompt(response.data);
      
      // Calculate token estimate (rough approximation: 1 token ≈ 4 characters)
      const systemTokens = Math.ceil(response.data.systemPrompt.length / 4);
      const userTokens = Math.ceil(response.data.userPromptTemplate.length / 4);
      const totalTokens = systemTokens + userTokens;
      setTokenEstimate(totalTokens);
      
      // Calculate cost estimate for Claude Sonnet (example: $3 per 1M input tokens)
      const costPerToken = 3 / 1000000; // $3 per million tokens
      setCostEstimate(totalTokens * costPerToken);
      
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to generate preview' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotification({ type: 'success', message: `${label} copied to clipboard` });
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to copy text' });
    }
  };

  const formatCost = (cost: number): string => {
    if (cost < 0.001) {
      return `$${(cost * 1000).toFixed(4)}k`;
    }
    return `$${cost.toFixed(6)}`;
  };

  return (
    <div className="p-6">
      {/* Notification */}
      {notification.type && (
        <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
          notification.type === 'success' 
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
            : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          {notification.message}
        </div>
      )}

      <div className="space-y-6">
        {/* Help Section */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">What is Prompt Preview?</h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
            Prompt Preview lets you test how prompts are generated before using them in production:
          </p>
          <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 ml-4">
            <li>• See exactly what prompts the AI will receive</li>
            <li>• Verify variable substitutions are working correctly</li>
            <li>• Estimate token usage and costs before running analysis</li>
            <li>• Copy prompts to test in external AI tools</li>
            <li>• Debug prompt configuration issues</li>
          </ul>
          <p className="text-sm text-gray-900 dark:text-gray-100 mt-3 font-medium">
            This is especially useful for testing new templates or framework configurations.
          </p>
        </div>

        {/* Configuration Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Prompt Configuration
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Country
              </label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name} ({country.code})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Analysis Framework
              </label>
              <select
                value={selectedFramework}
                onChange={(e) => setSelectedFramework(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select framework...</option>
                {frameworks.map(framework => (
                  <option key={framework.code} value={framework.code}>
                    {framework.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Prompt Template
              </label>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select template...</option>
                {templates.map(template => (
                  <option key={template.code} value={template.code}>
                    {template.name} ({template.templateType})
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Analysis Tier
              </label>
              <select
                value={selectedTier}
                onChange={(e) => setSelectedTier(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="1">Tier 1 - Relevance Check</option>
                <option value="2">Tier 2 - Standard Analysis</option>
                <option value="3">Tier 3 - Critical Analysis</option>
              </select>
            </div>
          </div>
          
          <button
            onClick={generatePreview}
            disabled={loading || !selectedCountry || !selectedFramework || !selectedTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            Generate Preview
          </button>
        </div>

        {/* Preview Results */}
        {generatedPrompt && (
          <div className="space-y-6">
            {/* Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                <div>
                  <div className="text-sm font-medium text-blue-900">Token Estimate</div>
                  <div className="text-xs text-blue-700">Approximate count</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-blue-900">
                    {tokenEstimate.toLocaleString()}
                  </div>
                  <div className="text-xs text-blue-700">
                    tokens
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                <div>
                  <div className="text-sm font-medium text-green-900">Cost Estimate</div>
                  <div className="text-xs text-green-700">Claude Sonnet input</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-green-900">
                    {formatCost(costEstimate)}
                  </div>
                  <div className="text-xs text-green-700">
                    per analysis
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
                <div>
                  <div className="text-sm font-medium text-purple-900">Variables</div>
                  <div className="text-xs text-purple-700">Dynamic substitutions</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-purple-900">
                    {Object.keys(generatedPrompt.variables).length}
                  </div>
                  <div className="text-xs text-purple-700">
                    replaced
                  </div>
                </div>
              </div>
            </div>

            {/* System Prompt */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-gray-900 dark:text-white">System Prompt</h4>
                  <button
                    onClick={() => copyToClipboard(generatedPrompt.systemPrompt, 'System prompt')}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                </div>
              </div>
              <div className="p-4">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 rounded p-3 overflow-auto max-h-64">
                  {generatedPrompt.systemPrompt}
                </pre>
              </div>
            </div>

            {/* User Prompt Template */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-gray-900 dark:text-white">User Prompt Template</h4>
                  <button
                    onClick={() => copyToClipboard(generatedPrompt.userPromptTemplate, 'User prompt')}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  This template will be filled with actual article content and sent as the user message to the AI.
                </p>
              </div>
              <div className="p-4">
                <div 
                  className="text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 rounded p-3 overflow-auto max-h-64"
                  dangerouslySetInnerHTML={{ 
                    __html: highlightVariables(generatedPrompt.userPromptTemplate) 
                  }}
                />
              </div>
            </div>

            {/* What Gets Sent to AI */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 p-4">
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5" />
                What Actually Gets Sent to AI
              </h4>
              <div className="space-y-3 text-sm text-blue-800 dark:text-blue-200">
                <div>
                  <span className="font-medium">System Message:</span> The processed system prompt above (with all variables filled)
                </div>
                <div>
                  <span className="font-medium">User Message:</span> The user prompt template with:
                  <ul className="ml-4 mt-1 space-y-1">
                    <li>• <code>{'{{articleTitle}}'}</code> → Actual article title</li>
                    <li>• <code>{'{{articleContent}}'}</code> → Full article text (scraped if needed)</li>
                  </ul>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-800 rounded border-l-4 border-blue-500">
                  <span className="font-medium">Example final user message:</span>
                  <div className="mt-1 font-mono text-xs">
                    "Analyze this article for threats to Estonia:<br/>
                    <br/>
                    Title: Russia conducts military exercises near Estonian border<br/>
                    Content: [Full scraped article text here...]<br/>
                    <br/>
                    Provide risk scores for each category and explain your reasoning."
                  </div>
                </div>
              </div>
            </div>

            {/* Variables */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white">Variable Substitutions</h4>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(generatedPrompt.variables).map(([key, value]) => (
                    <div key={key} className="p-3 bg-gray-50 dark:bg-gray-900 rounded border">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                            {`{{${key}}}`}
                          </h5>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 break-words">
                            {value.length > 100 ? `${value.substring(0, 100)}...` : value}
                          </p>
                        </div>
                        <button
                          onClick={() => copyToClipboard(value, `Variable ${key}`)}
                          className="ml-2 p-1 text-gray-500 hover:text-blue-600 transition-colors"
                          title="Copy value"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Metadata */}
            {generatedPrompt.metadata && (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Generation Metadata</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Country:</span>
                    <p className="font-medium text-gray-900 dark:text-white">{generatedPrompt.metadata.countryName}</p>
                  </div>
                  {generatedPrompt.metadata.frameworkCode && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Framework:</span>
                      <p className="font-medium text-gray-900 dark:text-white">{generatedPrompt.metadata.frameworkCode}</p>
                    </div>
                  )}
                  {generatedPrompt.metadata.templateVersion && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Template Version:</span>
                      <p className="font-medium text-gray-900 dark:text-white">{generatedPrompt.metadata.templateVersion}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Generated:</span>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {new Date(generatedPrompt.metadata.generatedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        {!generatedPrompt && (
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">How to use Prompt Preview</h4>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
              <li>• Select a country, analysis framework, and prompt template</li>
              <li>• Click "Generate Preview" to see the final prompt with variable substitutions</li>
              <li>• Review token count and cost estimates before running analysis</li>
              <li>• Copy prompts to test in external AI tools or debug issues</li>
              <li>• Use this to validate prompt templates and framework configurations</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}